import React, { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import addhoc from 'addhoc';
import { useRouter } from 'next/router';
import pathToRegexp from 'path-to-regexp';
import { FormattedMessage } from 'react-intl';
import { appReadySelector } from 'src/selector/app';
import { restauantProfileSelector } from 'src/selector/setting';
import { permissionCodeSelector } from 'src/selector/user';
import { restaurantListSelector, isGroupUserSelector } from 'src/selector/app';
import { USER_KEYS, getSessionItem } from 'src/store/storage';
import { loadPermissionCodes, logout } from 'src/actions/user';
import { shouldValidatePermission } from 'src/utils/utils';
import BasicLayout from 'src/layouts/basic';
import GroupLayout from 'src/layouts/groupLayout';
import UnactivatedResult from '../UnactivatedResult';
import UnauthorizedResult from '../UnauthorizedResult';
import UnauthorizedRg from 'src/components/RestGroupReports/components/UnauthorizedRg.jsx';
import PageNotFound from '../PageNotFound';
import {
  PageAccount,
  PageDashboard,
  PageInternalMonthlyStatement,
  PageMenuSetup,
  PageWorkforce,
} from 'src/consts/routes';
import { MENU_SETUP, REPORTS_SALES, WORKFORCE } from 'src/consts/permissionCodes';
import routeMapping from 'src/consts/routeMapping';
import { fetchRestaurantProfile } from 'src/actions/setting';
import PageLoading from '../PageLoading';
import { rgPermissionsSelector } from 'src/selector/rgUser';
import { rgPermissionCodes } from 'src/consts/rgMenu';

const ignorePaths = [PageInternalMonthlyStatement];

function handlePermissionForSpecialPaths(pathname) {
  if (pathname === PageDashboard) return REPORTS_SALES;
  if (pathname === PageMenuSetup) return MENU_SETUP;
  if (pathname.includes(PageWorkforce)) return WORKFORCE;
  return null;
}

export const withContainer = addhoc((getWrappedComponent) => {
  const ref = useRef();
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    function resizeObserverHandler(entries) {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect?.width || 0);
      }
    }

    const debouncedHandler = debounce(resizeObserverHandler, 150);
    const resizeObserver = new ResizeObserver(debouncedHandler);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.unobserve(container);
    };
  }, []);

  return <div ref={ref}>{getWrappedComponent({ containerWidth })}</div>;
}, 'withContainer');

export const withPermissionCheck = addhoc((getWrappedComponent) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const appReady = useSelector(appReadySelector);
  const { isLoading: isLoadingProfile, restaurantInfo } = useSelector(restauantProfileSelector);
  const { permissionCodes, isLoadDone, isLoading } = useSelector(permissionCodeSelector);
  const isGroup = useSelector(isGroupUserSelector);

  const shouldValidateRoute = useMemo(() => shouldValidatePermission(restaurantInfo), [restaurantInfo]);

  useEffect(() => {
    if (!appReady) return;
    const token = getSessionItem(USER_KEYS.token);
    if (!token) {
      dispatch(logout());
      return;
    }

    fetchPermissionCode();
    dispatch(fetchRestaurantProfile());
  }, [appReady]);

  const fetchPermissionCode = () => {
    dispatch(loadPermissionCodes());
  };

  if (!appReady) return null;

  if (isGroup) {
    return (
      <GroupLayout>
        <PageNotFound />
      </GroupLayout>
    );
  }

  if (isLoadingProfile) return null;

  if (isLoading && !isLoadDone) {
    return (
      <BasicLayout>
        <PageLoading absoluteCenter={true} background="transparent" />
      </BasicLayout>
    );
  }

  if (!isLoading && !isLoadDone) {
    return (
      <BasicLayout>
        <div style={{ paddingTop: 100, fontSize: 16, textAlign: 'center' }}>
          <a onClick={fetchPermissionCode}>
            <FormattedMessage id="reload-permissionCodes" />
          </a>
        </div>
      </BasicLayout>
    );
  }

  if (!shouldValidateRoute || ignorePaths.includes(pathname)) return getWrappedComponent();

  const restaurantActivated = JSON.parse(getSessionItem(USER_KEYS.activated));

  const { pathname } = router;
  let element;

  if (!restaurantActivated && (pathname === PageAccount || pathname.includes('/settings'))) {
    element = <UnactivatedResult />;
  } else if (!permissionCodes.length) {
    element = <UnauthorizedResult router={router} />;
  } else {
    let permissionCode = handlePermissionForSpecialPaths(pathname);

    if (!permissionCode) {
      permissionCode = Object.keys(routeMapping).find((key) => {
        const route = routeMapping[key];
        if (!route.path) return false;
        if (route.exact) return route.path === pathname;
        return pathToRegexp(route.path).test(pathname);
      });
    }

    if (permissionCode) {
      let noPermission = !permissionCodes.find((_) => _.toLowerCase() === permissionCode);
      const { distributionMode } = routeMapping[permissionCode] || {};

      if (!noPermission && distributionMode) {
        const modes = (restaurantInfo.restaurant_contracts || []).map((_) => _.distribution_mode);
        noPermission = !modes.includes(distributionMode);
      }

      if (noPermission) {
        element = <UnauthorizedResult router={router} />;
      }
    }
  }

  if (element) {
    return <BasicLayout>{element}</BasicLayout>;
  }

  return getWrappedComponent();
}, 'withPermissionCheck');

export const withGroupPermissionCheck = addhoc((getWrappedComponent) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const appReady = useSelector(appReadySelector);
  const isGroup = useSelector(isGroupUserSelector);
  const { curPermissions } = useSelector(rgPermissionsSelector);

  const { isLoading: isRestLoading } = useSelector(restaurantListSelector);

  useEffect(() => {
    if (!appReady) return;
    const token = getSessionItem(USER_KEYS.token);
    if (!token) {
      dispatch(logout());
    }
  }, [appReady]);

  if (!appReady) {
    return null;
  }

  // loading page
  if (isRestLoading || !curPermissions) {
    return (
      <GroupLayout>
        <PageLoading absoluteCenter={true} background="transparent" />
      </GroupLayout>
    );
  }

  if (!isGroup) {
    return (
      <BasicLayout>
        <PageNotFound />
      </BasicLayout>
    );
  }

  const curPermissionCode = rgPermissionCodes[router.pathname];
  if (curPermissionCode && !curPermissions.includes(curPermissionCode)) {
    const noPermissions = !curPermissions.length;
    return (
      <GroupLayout>
        <UnauthorizedRg
          router={router}
          isTitleEmpty={noPermissions}
          subTitleId={noPermissions ? 'rg.action.noMenu' : 'page403.subtitle'}
          showBackBtn={!noPermissions}
        />
      </GroupLayout>
    );
  }
  return getWrappedComponent();
}, 'withGroupPermissionCheck');

// use as React.createElement(withFitContent, { ...props }, children);
export const withFitContent = withContainer(({ containerWidth, children }) => {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || !containerWidth) return;
    const clientWidth = element.clientWidth;
    if (clientWidth <= containerWidth) return;
    const scale = Number(containerWidth / clientWidth).toFixed(2);
    element.style.transform = `scale(${scale})`;
    element.style.transformOrigin = '0 50%';
    element.style.fontWeight = 'bold';
  }, [children, containerWidth]);

  return (
    <div ref={ref} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
      {children}
    </div>
  );
});
